// src/Hooks/useKmlFirebase.js

import { useEffect } from 'react';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../Firebase/firebase';
import {
  collection,
  onSnapshot,
  writeBatch,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
  getDoc,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useIcons } from '../Contexts/IconContext';

// --- HELPERS ---
const getLocalYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 15));

const INFO_FIELD_MAPPING = {
  DATE: 'date',
  'LOCATION ID': 'locationId',
  TITLE: 'title',
  'WEB SITE': 'website',
  'LOCATION ADDRESS': 'address',
  CITY: 'city',
  STATE: 'state',
  'POSTCODE/ZIP': 'zip',
  COUNTRY: 'country',
  'CLIENT NAME': 'clientName',
  CATEGORY: 'categoryId',
  REGION: 'regionId',
  CONTROLLER: 'controller',
  KEYWORDS: 'keywords',
};

const TEXT_INFO_FIELDS = [
  'LOCATION ID',
  'TITLE',
  'WEB SITE',
  'LOCATION ADDRESS',
  'CITY',
  'STATE',
  'POSTCODE/ZIP',
  'COUNTRY',
  'CLIENT NAME',
  'CONTROLLER',
  'KEYWORDS',
];

export const useKmlFirebase = (stateStore) => {
  const { state, setters, uiActions } = stateStore;
  const { defaultIconID } = useIcons();

  // --- FIRESTORE LISTENERS ---

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedCats = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedCats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setCategories(fetchedCats);
      },
      (error) => console.error('Error fetching categories:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        const fetchedRegions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setRegions(fetchedRegions);
      },
      (error) => console.error('Error fetching regions:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'markerIcons'),
      (snapshot) => {
        const fetchedIcons = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedIcons.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setMarkerIcons(fetchedIcons);
      },
      (error) => console.error('Error fetching marker icons:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'MakePublic'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setters.setMakePublicDefaultDays(data.makePublicDefault || 0);
        }
      },
      (error) => console.error('Error fetching MakePublic settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'TextFormatting'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setters.setMakeUppercase(
            data.makeUppercase !== undefined ? !!data.makeUppercase : true,
          );
        } else {
          setters.setMakeUppercase(true);
        }
      },
      (error) => console.error('Error fetching TextFormatting settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'kmlImports'),
      (snapshot) => {
        const historyData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        historyData.sort((a, b) => new Date(b.importDate) - new Date(a.importDate));
        setters.setImportHistory(historyData);
        setters.setIsLoadingHistory(false);

        setters.setSelectedHistoryIds((prev) =>
          prev.filter((id) => historyData.find((h) => h.id === id)),
        );
      },
      (error) => {
        console.error('Error fetching import history:', error);
        setters.setIsLoadingHistory(false);
      },
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- FIRESTORE ACTIONS ---

  const executeSave = async (filename) => {
    if (!state.parsedData || state.parsedData.length === 0) return;

    let dataToSave = [];
    if (state.selectedImportIds.length > 0) {
      dataToSave = state.parsedData.filter(
        (item) => state.selectedImportIds.includes(item.id) && !item.isImported,
      );
    } else {
      dataToSave = state.parsedData.filter((item) => !item.isImported);
    }

    if (dataToSave.length === 0) return;

    setters.setIsProcessing(true);

    try {
      // --- PROCESS DEFERRED REINSTATES PRIOR TO WRITE ---
      if (state.pendingReinstateBatchIds?.length > 0) {
        if (setters.setProgressMessage)
          setters.setProgressMessage('Cleaning up reinstated batches...');
        await yieldToMain();

        for (const batchId of state.pendingReinstateBatchIds) {
          const groupsQuery = query(
            collection(db, 'markerGroups'),
            where('importBatchId', '==', batchId),
          );
          const groupsSnapshot = await getDocs(groupsQuery);
          const markersQuery = query(
            collection(db, 'markers'),
            where('importBatchId', '==', batchId),
          );
          const markersSnapshot = await getDocs(markersQuery);

          const storageDeletePromises = [];
          groupsSnapshot.docs.forEach((docSnap) => {
            const groupId = docSnap.id;
            storageDeletePromises.push(
              listAll(ref(storage, `marker_images/${groupId}`))
                .then((res) =>
                  Promise.all(
                    res.items.map((item) => deleteObject(item).catch(() => {})),
                  ),
                )
                .catch(() => {}),
            );
            storageDeletePromises.push(
              listAll(ref(storage, `photos/${groupId}`))
                .then((res) =>
                  Promise.all(
                    res.items.map((item) => deleteObject(item).catch(() => {})),
                  ),
                )
                .catch(() => {}),
            );
            storageDeletePromises.push(
              listAll(ref(storage, `documents/${groupId}`))
                .then((res) =>
                  Promise.all(
                    res.items.map((item) => deleteObject(item).catch(() => {})),
                  ),
                )
                .catch(() => {}),
            );
          });
          if (storageDeletePromises.length > 0) await Promise.all(storageDeletePromises);

          const allDocsToDelete = [...groupsSnapshot.docs, ...markersSnapshot.docs];
          for (let i = 0; i < allDocsToDelete.length; i += 400) {
            const chunk = allDocsToDelete.slice(i, i + 400);
            const deleteBatch = writeBatch(db);
            chunk.forEach((d) => deleteBatch.delete(d.ref));
            await deleteBatch.commit();
            await yieldToMain();
          }
          await deleteDoc(doc(db, 'kmlImports', batchId));
        }
        setters.setPendingReinstateBatchIds([]);
      }

      if (setters.setProgressMessage) setters.setProgressMessage('Importing markers...');
      const batch = writeBatch(db);
      const currentDate = new Date().toISOString();
      const importBatchId = uuidv4();

      const cleanCustomRows = (state.customRows || []).filter(
        (r) => r.id !== 'prop_images',
      );
      const cleanRowMappings = { ...state.rowMappings };
      delete cleanRowMappings['prop_images'];
      const cleanRowSubMappings = { ...state.rowSubMappings };
      delete cleanRowSubMappings['prop_images'];
      const cleanRowStaticValues = { ...state.rowStaticValues };
      delete cleanRowStaticValues['prop_images'];

      const importRecord = {
        filename:
          filename || state.currentFilename || state.originalFilename || 'Unknown KML',
        importDate: currentDate,
        markerCount: dataToSave.length,
        categoryId: state.globalCategory || null,
        status: 'COMPLETED',
        templateSnapshot: {
          customRows: cleanCustomRows,
          rowMappings: cleanRowMappings,
          rowSubMappings: cleanRowSubMappings,
          rowStaticValues: cleanRowStaticValues,
          globalCategory: state.globalCategory || '',
        },
      };
      batch.set(doc(db, 'kmlImports', importBatchId), importRecord);

      for (const item of dataToSave) {
        const markerId = uuidv4();
        const groupId = uuidv4();

        let finalIconID = defaultIconID || null;
        let finalHoverContent = '';
        let rawHoverImageUrl = null;
        let extractedDescription = '';

        let mappedInfoFields = {};

        Object.keys(state.rowMappings).forEach((rowId) => {
          const target = state.rowMappings[rowId];
          const subTarget = state.rowSubMappings[rowId];

          let propName = '';
          let expectedColor = null;

          if (rowId === 'prop_name') propName = 'Name';
          else if (rowId.startsWith('prop_color_')) {
            propName = 'Color';
            expectedColor = '#' + rowId.substring(11);
          } else if (rowId === 'prop_color') propName = 'Color';
          else if (rowId === 'prop_richtext') propName = 'Rich Text';
          else if (rowId.startsWith('custom_')) {
            const customRow = state.customRows.find((r) => r.id === rowId);
            propName = customRow ? customRow.name : '-';
          }

          if (
            expectedColor &&
            (item.color || '').toLowerCase() !== expectedColor.toLowerCase()
          ) {
            return;
          }

          let valueToApply = '';
          const normPropName = propName.toLowerCase();

          if (normPropName === 'name') valueToApply = item.name || '';
          else if (normPropName === 'color') valueToApply = item.color || '';
          else if (normPropName === 'rich text') valueToApply = item.description || '';
          else if (propName === '-') {
            valueToApply = state.rowStaticValues[rowId] || '';
          } else valueToApply = propName;

          if (!target) return;

          if (target === 'ICON' && subTarget) {
            finalIconID = subTarget;
          } else if (target === 'VISIBILITY' && subTarget) {
            if (subTarget === 'PRIVATE') {
              mappedInfoFields['visibility'] = 'Private';
            } else if (subTarget === 'RESTRICTED') {
              mappedInfoFields['visibility'] = 'Restricted';
            } else if (subTarget === 'PUBLIC FROM') {
              mappedInfoFields['visibility'] = `Public From ${valueToApply}`;
            } else if (subTarget === 'PUBLIC') {
              mappedInfoFields['visibility'] = 'Public';
            }
          } else if (target === 'HOVER TEXT') {
            if (state.makeUppercase && typeof valueToApply === 'string' && valueToApply) {
              finalHoverContent = valueToApply.toUpperCase();
            } else {
              finalHoverContent = valueToApply;
            }
          } else if (target === 'HOVER PHOTO') {
            rawHoverImageUrl =
              item.imageUrls && item.imageUrls.length > 0 ? item.imageUrls[0] : null;
          } else if (target === 'DESCRIPTION') {
            extractedDescription = valueToApply;
          } else if (target === 'INFO' && subTarget) {
            const dbFieldKey = INFO_FIELD_MAPPING[subTarget];
            if (dbFieldKey) {
              let finalVal = valueToApply;
              if (
                state.makeUppercase &&
                TEXT_INFO_FIELDS.includes(subTarget) &&
                typeof finalVal === 'string'
              ) {
                finalVal = finalVal.toUpperCase();
              }
              mappedInfoFields[dbFieldKey] = finalVal;
            }
          }
        });

        const photosArray = [];
        const finalDesc = extractedDescription || item.description || '';

        if (finalDesc) {
          photosArray.push({
            description: finalDesc,
            fileName: '',
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: 'text',
            uploadedAt: new Date().toISOString(),
            url: '',
          });
        }

        let uploadedImageUrl = null;
        if (rawHoverImageUrl) {
          let cleanUrl = rawHoverImageUrl.replace('{size}', '800');

          if (cleanUrl.startsWith('http')) {
            try {
              const response = await fetch(cleanUrl);
              if (response.ok) {
                const blob = await response.blob();
                const imageRef = ref(
                  storage,
                  `marker_images/${groupId}/hover_${Date.now()}.jpg`,
                );
                await uploadBytes(imageRef, blob);
                uploadedImageUrl = await getDownloadURL(imageRef);
              } else {
                uploadedImageUrl = cleanUrl.includes('google.com') ? null : cleanUrl;
              }
            } catch (error) {
              uploadedImageUrl = cleanUrl.includes('google.com') ? null : cleanUrl;
            }
          } else {
            uploadedImageUrl = rawHoverImageUrl;
          }
        }

        const geometryData = {
          position: { lat: item.lat, lng: item.lng },
          iconID: finalIconID,
          markerGroupID: groupId,
          importBatchId: importBatchId,
        };
        batch.set(doc(db, 'markers', markerId), geometryData);

        const groupData = {
          markerID: markerId,
          categoryId: mappedInfoFields.categoryId || state.globalCategory || null,
          hoverContent: finalHoverContent,
          hoverImage: uploadedImageUrl,
          date: mappedInfoFields.date || getLocalYYYYMMDD(),
          lastModified: currentDate,
          importBatchId: importBatchId,
          visibility: mappedInfoFields.visibility || 'Public',
          iconID: finalIconID,
          ...mappedInfoFields,
        };

        if (!groupData.title && item.name) {
          groupData.title = state.makeUppercase ? item.name.toUpperCase() : item.name;
        }

        if (item.color) groupData.color = item.color;
        if (photosArray.length > 0) groupData.photos = photosArray;

        batch.set(doc(db, 'markerGroups', groupId), groupData);
      }

      await batch.commit();

      const importedIds = dataToSave.map((d) => d.id);

      const remainingUnimportedCount = state.parsedData.filter(
        (item) => !item.isImported && !importedIds.includes(item.id),
      ).length;

      if (remainingUnimportedCount === 0) {
        uiActions.handleClearData(false);
        setters.setViewMode('HISTORY_VIEW');
        setters.setAlertState({
          open: true,
          message: `Successfully imported the final ${dataToSave.length} markers! The import queue is now clear.`,
          severity: 'success',
        });
      } else {
        setters.setParsedData((prev) =>
          prev.map((item) =>
            importedIds.includes(item.id)
              ? { ...item, isImported: true, importBatchId: importBatchId }
              : item,
          ),
        );
        setters.setSelectedImportIds([]);

        setters.setConfirmDialog({
          open: true,
          message: `Successfully imported ${dataToSave.length} markers! You still have ${remainingUnimportedCount} unimported markers in the queue. Do you want to clear the queue and start over?`,
          severity: 'success',
          confirmText: 'Clear Data',
          cancelText: 'Keep Data',
          onConfirm: () => {
            uiActions.handleClearData(false);
            setters.setViewMode('HISTORY_VIEW');
            setters.setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
        });
      }
    } catch (error) {
      console.error('Error saving KML data:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      if (setters.setProgressMessage) setters.setProgressMessage('');
    }
  };

  const executeDeleteBatches = async () => {
    if (state.selectedHistoryIds.length === 0) return;

    setters.setIsProcessing(true);

    try {
      let totalDeleted = 0;

      for (const batchId of state.selectedHistoryIds) {
        const groupsQuery = query(
          collection(db, 'markerGroups'),
          where('importBatchId', '==', batchId),
        );
        const groupsSnapshot = await getDocs(groupsQuery);

        const markersQuery = query(
          collection(db, 'markers'),
          where('importBatchId', '==', batchId),
        );
        const markersSnapshot = await getDocs(markersQuery);

        const storageDeletePromises = [];

        groupsSnapshot.docs.forEach((docSnap) => {
          const groupId = docSnap.id;

          const hoverFolderRef = ref(storage, `marker_images/${groupId}`);
          const hoverFolderDeletePromise = listAll(hoverFolderRef)
            .then((res) => {
              const itemDeletes = res.items.map((itemRef) =>
                deleteObject(itemRef).catch(() => {}),
              );
              return Promise.all(itemDeletes);
            })
            .catch(() => {});
          storageDeletePromises.push(hoverFolderDeletePromise);

          const photosFolderRef = ref(storage, `photos/${groupId}`);
          const folderDeletePromise = listAll(photosFolderRef)
            .then((res) => {
              const itemDeletes = res.items.map((itemRef) =>
                deleteObject(itemRef).catch(() => {}),
              );
              return Promise.all(itemDeletes);
            })
            .catch(() => {});

          storageDeletePromises.push(folderDeletePromise);
        });

        if (storageDeletePromises.length > 0) {
          await Promise.all(storageDeletePromises);
        }

        const allDocsToDelete = [...groupsSnapshot.docs, ...markersSnapshot.docs];
        const chunkSize = 400;

        for (let i = 0; i < allDocsToDelete.length; i += chunkSize) {
          const chunk = allDocsToDelete.slice(i, i + chunkSize);
          const deleteBatch = writeBatch(db);
          chunk.forEach((d) => {
            deleteBatch.delete(d.ref);
          });
          await deleteBatch.commit();
        }

        totalDeleted += allDocsToDelete.length / 2;

        await deleteDoc(doc(db, 'kmlImports', batchId));
      }

      setters.setAlertState({
        open: true,
        message: `Successfully deleted ${totalDeleted} markers and reversed import.`,
        severity: 'success',
      });
      setters.setSelectedHistoryIds([]);
    } catch (error) {
      console.error('Error deleting batch:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to reverse import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
    }
  };

  const executeMoveBatches = async () => {
    const { selectedHistoryIds, markerIcons } = state;
    if (!selectedHistoryIds || selectedHistoryIds.length === 0) return;

    setters.setIsProcessing(true);
    if (setters.setProgressMessage) setters.setProgressMessage('Moving batches...');
    await yieldToMain();

    try {
      let totalMoved = 0;

      for (const batchId of selectedHistoryIds) {
        const kmlBatchRef = doc(db, 'kmlImports', batchId);
        const kmlBatchSnap = await getDoc(kmlBatchRef);

        if (kmlBatchSnap.exists()) {
          const batchData = kmlBatchSnap.data();

          if (batchData.templateSnapshot) {
            const snap = batchData.templateSnapshot;
            const newCustomRows = [];
            const newRowMappings = {};
            const newRowSubMappings = {};
            const newRowStaticValues = {};

            let hasColorIcons = false;

            // 1. Clean out user-typed duplicates and target color/image rows
            const intrinsicNames = [
              'title',
              'lat, lng',
              'lat,lng',
              'rich text',
              'description',
              'color',
              'icon',
              'name',
              'position',
              'images',
              'photos',
            ];

            if (snap.customRows) {
              snap.customRows.forEach((row) => {
                if (row.id && row.id === 'prop_images') {
                  // completely ignore images
                } else if (row.id && row.id.startsWith('prop_color_')) {
                  hasColorIcons = true;
                } else if (row.id && row.id.startsWith('custom_')) {
                  if (!row.name || !intrinsicNames.includes(row.name.toLowerCase())) {
                    newCustomRows.push(row);
                  }
                } else if (!newCustomRows.some((r) => r.id === row.id)) {
                  newCustomRows.push(row);
                }
              });
            }

            // 2. Transfer legitimate custom mappings
            Object.keys(snap.rowMappings || {}).forEach((key) => {
              if (key === 'prop_images') {
                // ignore
              } else if (key.startsWith('prop_color_')) {
                hasColorIcons = true;
              } else if (newCustomRows.some((r) => r.id === key)) {
                newRowMappings[key] = snap.rowMappings[key];
              }
            });

            Object.keys(snap.rowSubMappings || {}).forEach((key) => {
              if (
                key !== 'prop_images' &&
                !key.startsWith('prop_color_') &&
                newCustomRows.some((r) => r.id === key)
              ) {
                newRowSubMappings[key] = snap.rowSubMappings[key];
              }
            });

            Object.keys(snap.rowStaticValues || {}).forEach((key) => {
              if (
                key !== 'prop_images' &&
                !key.startsWith('prop_color_') &&
                newCustomRows.some((r) => r.id === key)
              ) {
                newRowStaticValues[key] = snap.rowStaticValues[key];
              }
            });

            // 3. SECURE NATIVE KML MAPPINGS FOR BATCH IMPORT

            // Native Name -> TITLE
            if (!newCustomRows.some((r) => r.id === 'prop_name')) {
              newCustomRows.push({ id: 'prop_name', name: 'TITLE', isCustom: true });
            }
            newRowMappings['prop_name'] = 'INFO';
            newRowSubMappings['prop_name'] = 'TITLE';

            // Native Position -> LAT,LNG
            if (!newCustomRows.some((r) => r.id === 'prop_position')) {
              newCustomRows.push({
                id: 'prop_position',
                name: 'LAT,LNG',
                isCustom: true,
              });
            }
            newRowMappings['prop_position'] = 'POSITION (LAT, LNG)';

            // Native Rich Text -> DESCRIPTION
            if (!newCustomRows.some((r) => r.id === 'prop_richtext')) {
              newCustomRows.push({
                id: 'prop_richtext',
                name: 'DESCRIPTION',
                isCustom: true,
              });
            }
            newRowMappings['prop_richtext'] = 'DESCRIPTION';

            // Consolidate Native Color icons into a single ICON column
            if (hasColorIcons) {
              if (!newCustomRows.some((r) => r.id === 'prop_icon')) {
                newCustomRows.push({ id: 'prop_icon', name: 'ICON', isCustom: true });
              }
              newRowMappings['prop_icon'] = 'ICON';
            }

            batchData.templateSnapshot = {
              ...snap,
              customRows: newCustomRows,
              rowMappings: newRowMappings,
              rowSubMappings: newRowSubMappings,
              rowStaticValues: newRowStaticValues,
            };

            // 4. Generate standard Excel column layout based on strictly defined mappings
            batchData.columns = newCustomRows
              .filter((r) => r.name && r.name !== '-')
              .map((r) => r.name);

            // 5. Transform internal data keys so they bind flawlessly to the Batch Import headers
            if (batchData.parsedData && Array.isArray(batchData.parsedData)) {
              batchData.parsedData = batchData.parsedData.map((row) => {
                const newRow = { ...row };

                newRow['TITLE'] = row.name || '';

                if (row.lat !== undefined && row.lng !== undefined) {
                  newRow['LAT,LNG'] = `${row.lat}, ${row.lng}`;
                }

                newRow['DESCRIPTION'] = row.description || row.cleanDescription || '';
                newRow['_hasRichTextFlag'] = row.hasRichText;

                if (hasColorIcons && row.color) {
                  const colorHex = row.color.replace('#', '').toUpperCase();
                  const colorPropId = `prop_color_${colorHex}`;
                  if (snap.rowMappings && snap.rowMappings[colorPropId] === 'ICON') {
                    const mappedIconId = snap.rowSubMappings[colorPropId];
                    const iconObj = markerIcons?.find((i) => i.id === mappedIconId);
                    newRow['ICON'] = iconObj ? iconObj.name : '';
                  }
                }

                return newRow;
              });
            }
          }

          const excelBatchRef = doc(db, 'excelImports', batchId);
          const moveBatch = writeBatch(db);
          moveBatch.set(excelBatchRef, batchData);
          moveBatch.delete(kmlBatchRef);
          await moveBatch.commit();
          totalMoved++;
        }
      }

      setters.setAlertState({
        open: true,
        message: `Success! Moved ${totalMoved} batch(es) to the Batch Import module.`,
        severity: 'success',
      });
      setters.setSelectedHistoryIds([]);
    } catch (error) {
      console.error('Error moving batches:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to move batches: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      if (setters.setProgressMessage) setters.setProgressMessage('');
    }
  };

  const executeReinstateBatch = async () => {
    const { selectedHistoryIds } = state;
    if (!selectedHistoryIds || selectedHistoryIds.length === 0) return;

    if (setters.setUploadProgress) setters.setUploadProgress({ current: 0, total: 0 });
    setters.setIsProcessing(true);
    if (setters.setProgressMessage) setters.setProgressMessage('Preparing batch data...');

    await yieldToMain();

    try {
      let allRestoredData = [];
      let totalMarkers = 0;

      for (const batchId of selectedHistoryIds) {
        const groupsQuery = query(
          collection(db, 'markerGroups'),
          where('importBatchId', '==', batchId),
        );
        const groupsSnapshot = await getDocs(groupsQuery);

        const markersQuery = query(
          collection(db, 'markers'),
          where('importBatchId', '==', batchId),
        );
        const markersSnapshot = await getDocs(markersQuery);

        totalMarkers += groupsSnapshot.docs.length;

        for (let idx = 0; idx < groupsSnapshot.docs.length; idx++) {
          const gDoc = groupsSnapshot.docs[idx];
          const gData = gDoc.data();
          const mDoc = markersSnapshot.docs.find(
            (m) => m.data().markerGroupID === gDoc.id,
          );
          const mData = mDoc ? mDoc.data() : null;

          let description = '';

          if (gData.photos && Array.isArray(gData.photos)) {
            const textPhoto = gData.photos.find((p) => p.type === 'text');
            if (textPhoto) {
              description = textPhoto.description;
            }
          }

          const restoredRow = {
            id: `restored_${Date.now()}_${batchId}_${idx}`,
            isImported: false,
            name: gData.title || gData.name || '',
            description: description || '',
            lat: mData?.position?.lat || 0,
            lng: mData?.position?.lng || 0,
            color: gData.color || '',
            hasRichText: /<[a-z][\s\S]*>/i.test(description || ''),
            originalMarkerGroupID: gDoc.id,
          };

          allRestoredData.push(restoredRow);
        }
      }

      allRestoredData.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // Restore template snapshot mappings from the last batch processed
      const lastBatchId = selectedHistoryIds[selectedHistoryIds.length - 1];
      const batchRecord = state.importHistory?.find((h) => h.id === lastBatchId);

      if (batchRecord?.templateSnapshot) {
        const cleanCustomRows = (batchRecord.templateSnapshot.customRows || []).filter(
          (r) => r.id !== 'prop_images' && r.name !== 'Images',
        );
        setters.setCustomRows(cleanCustomRows);

        const cleanMappings = { ...batchRecord.templateSnapshot.rowMappings };
        delete cleanMappings['prop_images'];
        setters.setRowMappings(cleanMappings);

        const cleanSubMappings = { ...batchRecord.templateSnapshot.rowSubMappings };
        delete cleanSubMappings['prop_images'];
        setters.setRowSubMappings(cleanSubMappings);

        const cleanStatic = { ...batchRecord.templateSnapshot.rowStaticValues };
        delete cleanStatic['prop_images'];
        setters.setRowStaticValues(cleanStatic);

        if (batchRecord.templateSnapshot.globalCategory) {
          setters.setGlobalCategory(batchRecord.templateSnapshot.globalCategory);
        }
      } else {
        setters.setCustomRows([]);
        setters.setRowMappings({});
        setters.setRowSubMappings({});
        setters.setRowStaticValues({});
        setters.setGlobalCategory('');
      }

      if (batchRecord?.filename) {
        setters.setCurrentFilename(batchRecord.filename);
      }

      setters.setPendingReinstateBatchIds((prev) => {
        const newIds = new Set([...(prev || []), ...selectedHistoryIds]);
        return Array.from(newIds);
      });

      setters.setParsedData((prev) => {
        const combined = [...prev, ...allRestoredData];
        return combined.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });

      setters.setSelectedImportIds([]);
      setters.setSelectedHistoryIds([]);
      setters.setViewMode('DATA_VIEW');
      setters.setAlertState({
        open: true,
        message: `Successfully reinstated ${totalMarkers} markers to the grid.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error reinstating batch:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to reinstate import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      if (setters.setProgressMessage) setters.setProgressMessage('');
    }
  };

  return {
    executeSave,
    executeDeleteBatches,
    executeMoveBatches,
    executeReinstateBatch,
  };
};
